import React, { useRef, type KeyboardEvent, type ReactElement } from "react";
import { CalendarDays, CloudOff, CloudUpload, Repeat2 } from "lucide-react";
import { Button, cn } from "@ndd/ui";
import {
  MIRROR_FAILED_HINT,
  MIRROR_FAILED_TEXT,
  MIRROR_PENDING_HINT,
  MIRROR_PENDING_TEXT,
} from "../../lib/mirror-state.js";
import {
  BLOCK_TONE_CLASS,
  BREAK_HATCH,
  WEEK_HOUR_PX,
  arrowTarget,
  hoursStartLabel,
  type GridArrow,
  type WeekBlockLayout,
  type WeekColumn,
  type WeekGridLayout,
} from "../../lib/rd-schedule-week.js";
import type { RdScheduleBlock } from "../../../../shared/rd-schedule.js";

// NO-874: the Week view's grid (ui-componentize: time gutter, day columns,
// blocks, now line). Presentational: the layout comes from
// lib/rd-schedule-week.ts, the colours from theme tokens and status pairs, and
// opening a block hands its appointment id to `onOpenDetails`, the seam the
// details panel (NO-875) plugs into. Relative imports only so `tsx --test`
// renders it.

const COLUMNS = "grid grid-cols-[4rem_repeat(var(--week-days),minmax(0,1fr))]";
const ARROWS: readonly string[] = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];

export interface WeekGridProps {
  layout: WeekGridLayout;
  /** A visit block was activated (click or Enter). */
  onOpenDetails: (appointmentId: string) => void;
  /** The RD's own blocked time was activated (NO-877); absent leaves it plain. */
  onOpenBlock?: (block: RdScheduleBlock) => void;
  /** The appointment whose details panel is open; its block takes a ring. */
  selectedId?: string | null;
  /**
   * A new RD with no hours: the card over the grid. Null hides the card; a
   * card with a null `onSetHours` has no button (the Availability gate is closed).
   */
  emptyCard: {
    onSetHours: (() => void) | null;
    /** NO-907: the Monday saved hours start, when they start after this week. */
    startsOn: string | null;
    onOpenWeek: (week: string) => void;
  } | null;
  /** A neighbouring week is loading; the grid stays, dimmed. */
  busy?: boolean;
}

/** The grid is a named group; it takes programmatic focus when Remove closes onto a gone block. */
const GRID_GROUP = {
  role: "group",
  "aria-label": "Week calendar",
  tabIndex: -1,
  "data-week-grid": "",
} as const;

export function WeekGrid(props: WeekGridProps): ReactElement {
  const { layout, onOpenDetails, onOpenBlock } = props;
  const { emptyCard, busy = false, selectedId = null } = props;
  const gridRef = useRef<HTMLDivElement | null>(null);

  // Tab reaches every visit block, and every stored blocked-time block once
  // Remove is wired; the arrow keys then move between visits only.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (!ARROWS.includes(event.key)) return;

    const current = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-block-key]"
    );

    if (!current?.dataset.blockKey) return;

    const next = arrowTarget(
      layout.blocks,
      current.dataset.blockKey,
      event.key as GridArrow
    );

    if (!next) return;

    event.preventDefault();
    const target = [
      ...(gridRef.current?.querySelectorAll<HTMLElement>("[data-block-key]") ?? []),
    ].find((element) => element.dataset.blockKey === next);
    target?.focus();
  };

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-border bg-card"
      aria-busy={busy}
    >
      <div className="overflow-x-auto">
        <div
          className={cn("min-w-[780px]", busy && "opacity-60")}
          style={{ ["--week-days" as string]: layout.columns.length }}
        >
          <DayHeaders columns={layout.columns} />
          <div
            ref={gridRef}
            {...GRID_GROUP}
            onKeyDown={onKeyDown}
            className={COLUMNS}
            style={{ height: layout.heightPx }}
          >
            <HourGutter layout={layout} />
            {layout.columns.map((column, index) => (
              <DayColumn
                key={column.date}
                column={column}
                index={index}
                layout={layout}
                selectedId={selectedId}
                onOpenDetails={onOpenDetails}
                onOpenBlock={onOpenBlock}
              />
            ))}
          </div>
        </div>
      </div>
      {emptyCard ? <EmptyGridCard card={emptyCard} /> : null}
    </div>
  );
}

function DayHeaders({ columns }: { columns: readonly WeekColumn[] }): ReactElement {
  return (
    <div className={cn(COLUMNS, "border-b border-border")}>
      <span />
      {columns.map((column) => (
        <div
          key={column.date}
          className="flex items-center gap-2 border-l border-border px-3 py-2.5"
        >
          <span className="text-label text-muted-foreground">{column.weekday}</span>
          <span
            className={cn(
              "font-sans font-bold",
              column.isToday
                ? "grid size-[28px] place-items-center rounded-full bg-primary text-body text-primary-foreground"
                : "text-title-sm"
            )}
            aria-current={column.isToday ? "date" : undefined}
          >
            {column.dayOfMonth}
          </span>
          <span
            title="Client-facing hours"
            className="ml-auto font-mono text-meta text-muted-strong"
          >
            {column.hoursLabel}
            <span className="sr-only"> client-facing</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function HourGutter({ layout }: { layout: WeekGridLayout }): ReactElement {
  return (
    <div className="relative" aria-hidden="true">
      {layout.hourLabels.map((hour) => (
        <span
          key={hour.hour}
          className="absolute right-2.5 whitespace-nowrap font-mono text-eyebrow text-muted-foreground"
          style={{ top: hour.topPx + 4 }}
        >
          {hour.label}
        </span>
      ))}
    </div>
  );
}

function DayColumn({
  column,
  index,
  layout,
  selectedId,
  onOpenDetails,
  onOpenBlock,
}: {
  column: WeekColumn;
  index: number;
  layout: WeekGridLayout;
  selectedId: string | null;
  onOpenDetails: (appointmentId: string) => void;
  onOpenBlock: ((block: RdScheduleBlock) => void) | undefined;
}): ReactElement {
  return (
    <div className="relative border-l border-border bg-muted">
      <DayBackdrop column={column} layout={layout} />
      {layout.blocks
        .filter((block) => block.dayIndex === index)
        .map((block) => (
          <Block
            key={block.key}
            block={block}
            selected={
              block.appointmentId !== null && block.appointmentId === selectedId
            }
            onOpenDetails={onOpenDetails}
            onOpenBlock={onOpenBlock}
          />
        ))}
      {layout.now && layout.now.dayIndex === index ? (
        <span
          role="img"
          aria-label={layout.now.label}
          className="absolute inset-x-0 z-[4] h-0.5 bg-foreground"
          style={{ top: layout.now.topPx }}
        >
          <span className="absolute -left-[5px] -top-1 size-2.5 rounded-full bg-foreground" />
        </span>
      ) : null}
    </div>
  );
}

/** The open (bookable) ranges and the hour lines behind a day's blocks. */
function DayBackdrop({
  column,
  layout,
}: {
  column: WeekColumn;
  layout: WeekGridLayout;
}): ReactElement {
  const px = (minutes: number): number =>
    ((minutes - layout.startHour * 60) / 60) * WEEK_HOUR_PX;

  return (
    <>
      {column.open.map((range) => (
        <span
          key={`${range.start}-${range.end}`}
          aria-hidden="true"
          className="absolute inset-x-0 bg-card"
          style={{ top: px(range.start), height: px(range.end) - px(range.start) }}
        />
      ))}
      {layout.hourLabels.map((hour, line) => (
        <span
          key={hour.hour}
          aria-hidden="true"
          className={cn(
            "absolute inset-x-0 z-[1] h-px",
            line === 0 ? "bg-transparent" : "bg-border"
          )}
          style={{ top: hour.topPx }}
        />
      ))}
    </>
  );
}

interface BlockAction {
  /** The attribute naming what the block opens. */
  attribute: Record<string, string>;
  label: string;
  open: () => void;
}

/**
 * What activating a block does: a visit opens its details, the RD's own
 * stored blocked time opens Remove (NO-877), and anything else, such as a
 * saved break drawn from Availability, is plain.
 */
function blockAction(
  block: WeekBlockLayout,
  onOpenDetails: (appointmentId: string) => void,
  onOpenBlock: ((block: RdScheduleBlock) => void) | undefined
): BlockAction | null {
  const own = block.ownBlock;

  if (own !== null && onOpenBlock) {
    return {
      attribute: { "data-block-id": own.id },
      label: `${block.ariaLabel}, opens remove`,
      open: () => onOpenBlock(own),
    };
  }

  const appointmentId = block.appointmentId;

  if (appointmentId === null) return null;

  return {
    attribute: { "data-appointment-id": appointmentId },
    label: block.ariaLabel,
    open: () => onOpenDetails(appointmentId),
  };
}

function Block({
  block,
  selected,
  onOpenDetails,
  onOpenBlock,
}: {
  block: WeekBlockLayout;
  selected: boolean;
  onOpenDetails: (appointmentId: string) => void;
  onOpenBlock: ((block: RdScheduleBlock) => void) | undefined;
}): ReactElement {
  const share = 100 / block.columns;
  const className = cn(
    "absolute z-[2] flex flex-col justify-center overflow-hidden rounded-[8px] px-[7px] py-0.5 text-left text-caption leading-4",
    BLOCK_TONE_CLASS[block.tone],
    selected && "z-[3] ring-2 ring-primary ring-offset-1 ring-offset-card"
  );
  const style = {
    top: block.topPx,
    height: block.heightPx,
    left: `calc(${block.column * share}% + 3px)`,
    width: `calc(${share}% - 6px)`,
    ...(block.tone === "break" ? { backgroundImage: BREAK_HATCH } : {}),
  };
  const action = blockAction(block, onOpenDetails, onOpenBlock);

  if (action === null) {
    return (
      <div className={className} style={style}>
        <BlockBody block={block} />
      </div>
    );
  }

  return (
    <button
      type="button"
      data-block-key={block.key}
      {...action.attribute}
      aria-label={action.label}
      onClick={action.open}
      className={cn(
        className,
        "cursor-pointer transition-[filter] hover:brightness-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
      )}
      style={style}
    >
      <BlockBody block={block} />
    </button>
  );
}

/**
 * A tall block's second line says the status note when there is one; otherwise
 * the type label, unless the Initial / S code tag beside it already says so.
 */
function tallTypeLabel(block: WeekBlockLayout): string {
  if (block.typeLabel === "Initial" && block.tags.includes("INITIAL")) return "";

  if (block.typeLabel === "S code" && block.tags.includes("S CODE")) return "";

  return block.typeLabel;
}

function BlockBody({ block }: { block: WeekBlockLayout }): ReactElement {
  const mirror = block.mirrorState === "pending" || block.mirrorState === "failed";
  // The corner marks sit side by side so a recurring Client with no next visit
  // keeps both; the text stops short of however many there are.
  const marks = [block.recurring, block.noNextVisit, mirror].filter(Boolean).length;
  const markPad = marks === 0 ? undefined : { paddingRight: marks * 21 };

  return (
    <>
      <span
        className={cn("block truncate", block.struck && "line-through")}
        style={markPad}
      >
        <b className="font-semibold">{block.title}</b>
        {!block.tall
          ? block.tags.map((tag) => (
              <React.Fragment key={tag}>
                {" "}
                <Tag text={tag} />
              </React.Fragment>
            ))
          : null}
      </span>
      {block.tall ? (
        <span
          className="flex items-center gap-1 overflow-hidden whitespace-nowrap"
          style={markPad}
        >
          {block.tags.map((tag) => (
            <Tag key={tag} text={tag} />
          ))}
          <span className="truncate">{block.note || tallTypeLabel(block)}</span>
        </span>
      ) : null}
      {marks > 0 ? (
        <span className="absolute right-[3px] top-[3px] flex gap-[3px]">
          {mirror ? <MirrorMark failed={block.mirrorState === "failed"} /> : null}
          {block.recurring ? (
            <span
              aria-hidden="true"
              className="grid size-[18px] place-items-center rounded-full bg-primary text-primary-foreground"
            >
              <Repeat2 className="size-[11px]" strokeWidth={3} />
            </span>
          ) : null}
          {block.noNextVisit ? (
            <span
              aria-hidden="true"
              className="grid size-[18px] place-items-center rounded-full bg-destructive font-sans text-micro font-extrabold leading-none text-destructive-foreground"
            >
              !!
            </span>
          ) : null}
        </span>
      ) : null}
    </>
  );
}

/**
 * NO-917's Healthie copy on a Week card: an icon, not the list row's tag, so a
 * short block keeps its name, and never yellow, which the calendar keeps for
 * cancelled. The details panel carries the sentence and Retry.
 */
function MirrorMark({ failed }: { failed: boolean }): ReactElement {
  const Icon = failed ? CloudOff : CloudUpload;

  return (
    <span
      data-mirror-state={failed ? "failed" : "pending"}
      title={failed ? MIRROR_FAILED_HINT : MIRROR_PENDING_HINT}
      className={cn(
        "grid size-[18px] place-items-center rounded-full border bg-card",
        failed
          ? "border-border-strong text-foreground"
          : "border-border text-muted-foreground"
      )}
    >
      <Icon aria-hidden="true" className="size-[11px]" strokeWidth={2.5} />
      <span className="sr-only">
        {failed ? MIRROR_FAILED_TEXT : MIRROR_PENDING_TEXT}
      </span>
    </span>
  );
}

function Tag({ text }: { text: string }): ReactElement {
  return (
    <span className="inline-block rounded-[4px] border border-current px-1 align-[1px] font-mono text-micro uppercase leading-[14px] tracking-[0.06em]">
      {text}
    </span>
  );
}

type EmptyCard = NonNullable<WeekGridProps["emptyCard"]>;

function EmptyGridCard({ card }: { card: EmptyCard }): ReactElement {
  if (card.startsOn === null) return <NoHoursCard onSetHours={card.onSetHours} />;

  return <HoursStartCard startsOn={card.startsOn} onOpenWeek={card.onOpenWeek} />;
}

/** NO-907: saved hours that start after this week: when, and a way there. */
function HoursStartCard({
  startsOn,
  onOpenWeek,
}: {
  startsOn: string;
  onOpenWeek: (week: string) => void;
}): ReactElement {
  return (
    <div className="absolute inset-x-0 bottom-0 top-[52px] z-[5] grid place-items-center p-6">
      <div className="flex max-w-[420px] flex-col items-center gap-2.5 rounded-lg border border-dashed border-border-strong bg-card px-7 py-9 text-center">
        <CalendarDays
          aria-hidden="true"
          className="size-9 text-muted-foreground"
          strokeWidth={1.25}
        />
        <h3 className="font-sans text-title font-semibold leading-title text-foreground">
          Your hours start {hoursStartLabel(startsOn)}
        </h3>
        <p className="text-muted-foreground">
          Your saved hours aren{"’"}t in effect this week.
        </p>
        <button
          type="button"
          onClick={() => onOpenWeek(startsOn)}
          className="text-body font-semibold text-primary underline decoration-1 underline-offset-[3px]"
        >
          Go to that week
        </button>
      </div>
    </div>
  );
}

/** The new-RD empty state over the grid: "Set your hours". */
function NoHoursCard({
  onSetHours,
}: {
  onSetHours: (() => void) | null;
}): ReactElement {
  return (
    <div className="absolute inset-x-0 bottom-0 top-[52px] z-[5] grid place-items-center p-6">
      <div className="flex max-w-[420px] flex-col items-center gap-2.5 rounded-lg border border-dashed border-border-strong bg-card px-7 py-9 text-center">
        <CalendarDays
          aria-hidden="true"
          className="size-9 text-muted-foreground"
          strokeWidth={1.25}
        />
        <h3 className="font-sans text-title font-semibold leading-title text-foreground">
          No hours set yet
        </h3>
        <p className="text-muted-foreground">
          Clients, the care team and Always AI can only book you inside your hours. Set
          them once and they repeat every week.
        </p>
        {onSetHours ? (
          <Button
            type="button"
            variant="press"
            size="compact"
            shape="pill"
            className="mt-1.5 px-[18px] text-body-sm"
            onClick={onSetHours}
          >
            Set your hours
          </Button>
        ) : null}
      </div>
    </div>
  );
}
